import { type ICSVExport } from '@principle-theorem/ng-shared';
import { ValueFormatter } from '@principle-theorem/principle-core';
import { MeasureFormatter } from '@principle-theorem/principle-core/interfaces';
import {
  IResolvedColumnProperty,
  ReportBuilderDataSource,
  ResolvedColumns,
} from '@principle-theorem/reporting';
import { slugify } from '@principle-theorem/shared';

interface ICSVColumn {
  id: string;
  label: string;
  data: IResolvedColumnProperty;
}

export class ReportBuilderCSV
  implements ICSVExport<Record<string, unknown>, Record<string, unknown>>
{
  defaultFileName: string;
  headers: string[] = [];
  columns: ICSVColumn[];

  constructor(
    private _selectedColumns: IResolvedColumnProperty[],
    defaultFileName = 'report-builder'
  ) {
    this.defaultFileName = slugify(defaultFileName);
    this.columns = this._selectedColumns.map((column) => ({
      id: column.definition.id,
      label: ResolvedColumns.getLabel(column),
      data: column,
    }));
    this.headers = this.columns.map((column) => column.label);
  }

  translate(
    records: Record<string, unknown>[]
  ): Record<string, unknown>[] | Promise<Record<string, unknown>[]> {
    return records.map((record) => this._translateRecord(record));
  }

  private _translateRecord(
    record: Record<string, unknown>
  ): Record<string, unknown> {
    return this.columns.reduce(
      (acc: Record<string, unknown>, column) => ({
        ...acc,
        [column.id]: this._getValue(column, record),
      }),
      {}
    );
  }

  private _getValue(
    column: ICSVColumn,
    record: Record<string, unknown>
  ): string | number {
    const accessor =
      column.data.property.measure.labelAccessor ??
      column.data.property.measure.dataAccessor;
    const value = accessor(record);

    const dataPoint = ReportBuilderDataSource.getDataPoint(column.data, record);
    if (!dataPoint) {
      return '';
    }

    if (
      dataPoint.formatter === MeasureFormatter.Number ||
      dataPoint.formatter === MeasureFormatter.Currency
    ) {
      return ValueFormatter.coerceToNumber(value);
    }
    return ValueFormatter.format(
      value,
      dataPoint.formatter,
      dataPoint.formatterValue
    );
  }
}
